<template>
  <router-view />
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { showToast } from '@inscribe/signal';

export default {
  data() {
    return {};
  },
  computed: {
    ...mapGetters(['getCachedCustomerById']),
  },
  methods: {
    ...mapActions([
      'fetchAndCacheCustomer',
      'fetchAndCacheFraudSummary',
      'fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID',
      'fetchAndCacheCustomerReviewAgentRunsForCustomerUUID',
      'fetchAndCacheDocumentReviewAgentRunsForCustomerUUID',
      'fetchAndCacheBusinessComplianceAgentRunsForCustomerUUID',
      'fetchAndCacheCustomerSignalsForCustomerUUID',
      'fetchAndCacheAggregatedDocumentResults',
    ]),
    handleCustomerFetchError(error) {
      if (error.response && error.response.status === 403) {
        showToast({
          text: error.response?.data?.message || '',
          isError: true,
        });
      }
    },
  },
  watch: {
    '$route.params.customer_id': {
      immediate: true,
      handler: async function (newCustomerId) {
        // IMPORTANT: This is the main data fetching location for all customer
        // pages, since this component wraps all sub-pages.

        // Fetch and cache customer data whenever the customer ID changes.
        // Meanwhile, support backwards compatibility with selectedCustomer
        // state, which is used in many places in the app.
        // If we already have cached customer data, use it and fetch
        // fresh data in the background.
        const cachedCustomer = this.getCachedCustomerById(newCustomerId);
        if (cachedCustomer) {
          this.$store.commit('setSelectedCustomer', cachedCustomer);
          this.$store.commit('setCustomerLoading', false);
          // Fetch fresh data in background
          this.fetchAndCacheCustomer(newCustomerId).catch(
            this.handleCustomerFetchError,
          );
        } else {
          // If not, fetch and cache the data.
          // Redirect to customers list if the customer is not found.
          this.$store.commit('setSelectedCustomer', undefined);
          this.$store.commit('setCustomerLoading', true);
          try {
            const customer = await this.fetchAndCacheCustomer(newCustomerId);
            this.$store.commit('setSelectedCustomer', customer);
          } catch (error) {
            this.handleCustomerFetchError(error);
            this.$router.push('/customers');
          } finally {
            this.$store.commit('setCustomerLoading', false);
          }
        }

        // Make supporting data fetches
        this.fetchAndCacheFraudSummary({ customerId: newCustomerId });
        this.fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID(
          newCustomerId,
        );
        this.fetchAndCacheCustomerReviewAgentRunsForCustomerUUID(newCustomerId);
        this.fetchAndCacheDocumentReviewAgentRunsForCustomerUUID(newCustomerId);
        this.fetchAndCacheBusinessComplianceAgentRunsForCustomerUUID(
          newCustomerId,
        );
        this.fetchAndCacheCustomerSignalsForCustomerUUID({
          customerId: newCustomerId,
        });
        this.fetchAndCacheAggregatedDocumentResults({
          customerId: newCustomerId,
        });
      },
    },
  },
  beforeUnmount() {
    this.$store.commit('setSelectedCustomer', undefined);
  },
};
</script>

<style lang="scss">
.red-banner {
  height: 50px;
  margin: 0 auto;
  background: red;
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px;
  z-index: 100000;
  opacity: 80%;
  color: #fff;
  text-align: center;
  border-radius: 5px 5px 0px 0px;
  font-size: 2.2vmin;

  &:hover {
    display: none;
  }
}
</style>
